// Pure helpers for the multi-track timeline model. No React — just the math that
// turns the stored EditorTimeline into laid-out positions and the small
// constructors/edits the editor applies. Kept here so TimelineEditor stays focused
// on wiring the library + preview.
import {
  ClipSegment, EditorTimeline, TLVideoClip, TLOverlayClip, TLAudioClip, TLAudioTrack, TranscriptSegment, VideoClip,
} from 'services/VideoEditingService';

// Short unique id for a timeline instance (a clip can appear more than once).
export function uid(): string {
  const c: any = (typeof crypto !== 'undefined' ? crypto : undefined);
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

// Trimmed length of a clip (out − in), clamped ≥ 0.
export function clipDur(c: { sourceInSec: number; sourceOutSec: number }): number {
  return Math.max(0, (c.sourceOutSec || 0) - (c.sourceInSec || 0));
}

export interface LaidVideoClip { clip: TLVideoClip; index: number; start: number; end: number; }

// Video clips play back-to-back: each starts where the previous trimmed clip ends.
export function videoLayout(timeline: EditorTimeline): LaidVideoClip[] {
  let cursor = 0;
  return (timeline.videoTrack.clips || []).map((clip, index) => {
    const start = cursor;
    cursor += clipDur(clip);
    return { clip, index, start, end: cursor };
  });
}

export function videoDuration(timeline: EditorTimeline): number {
  return (timeline.videoTrack.clips || []).reduce((acc, c) => acc + clipDur(c), 0);
}

// B-roll cutaways on the overlay track (optional field — older plans have none).
export function overlayClips(timeline: EditorTimeline): TLOverlayClip[] {
  return timeline.overlayTrack?.clips || [];
}

export interface LaidOverlayClip { clip: TLOverlayClip; index: number; start: number; end: number; }

// Overlay clips position freely, like audio.
export function overlayLayout(timeline: EditorTimeline): LaidOverlayClip[] {
  return overlayClips(timeline).map((clip, index) => {
    const start = clip.timelineStartSec || 0;
    return { clip, index, start, end: start + clipDur(clip) };
  });
}

// The B-roll clip whose picture covers timeline time t, or null. Clips on this row
// are kept from overlapping (see resolveOverlayOverlaps), so at most one can match; the
// reverse scan is retained so a plan saved before that rule still resolves to a single
// deterministic clip — the last in array order, i.e. the most recently added.
export function overlayCoverAt(timeline: EditorTimeline, t: number): LaidOverlayClip | null {
  const laid = overlayLayout(timeline);
  for (let i = laid.length - 1; i >= 0; i -= 1) {
    if (t >= laid[i].start && t < laid[i].end) return laid[i];
  }
  return null;
}

// Float slack: timeline positions come from pixel maths, so exact equality would
// report an overlap where two clips merely touch.
const OVERLAP_EPSILON = 0.001;

// Resolve B-roll overlaps by PUSHING, the way the main video track does.
//
// Two cutaways covering the same instant have no defined winner — whichever renders
// last simply hides the other, so the edit no longer says what it means, and the
// exported instructions describe a cut that cannot be performed. The row therefore
// holds one clip at a time.
//
// The main track never refuses a clip: its clips are sequential, so dropping one in
// displaces everything after it. This is the free-positioned equivalent. `anchorId` is
// the clip just dropped, moved or lengthened; anything it runs into is moved later,
// cascading so the clip pushed aside does not land on the next one either.
//
// NOTHING BEFORE THE ANCHOR MOVES, and a clip that was already RUNNING when the anchor
// landed counts as before it. Pushing one of those would send a clip the user can see
// playing — possibly from the very start of the row — forwards past the drop, leaving a
// hole where it used to be. Dropping a 2s cutaway at t=5 onto one occupying 0–10 moved
// that clip to 7 and emptied 0–5. So the anchor slides to the end of what it landed
// inside instead: the drop still succeeds, and the row keeps the shape the user was
// looking at. That is also why the caller clamps a LEFT resize rather than calling this —
// dragging a start earlier would otherwise shove neighbours backwards.
export function resolveOverlayOverlaps(clips: TLOverlayClip[], anchorId: string): TLOverlayClip[] {
  const anchor = clips.find((clip) => clip.id === anchorId);
  if (!anchor) return clips;
  const others = clips.filter((clip) => clip.id !== anchorId);

  // The clip already running at a given instant, if any.
  const straddlingAt = (at: number) => others.find((clip) => (
    (clip.timelineStartSec || 0) < at - OVERLAP_EPSILON
    && (clip.timelineStartSec || 0) + clipDur(clip) > at + OVERLAP_EPSILON
  ));

  // Slide past whatever was running at the drop point. Repeats because the new position
  // can land inside a further clip (only possible on a row saved with overlaps); each pass
  // moves strictly forward to a clip's end, so it terminates.
  let anchorStart = anchor.timelineStartSec || 0;
  for (let hit = straddlingAt(anchorStart); hit; hit = straddlingAt(anchorStart)) {
    anchorStart = (hit.timelineStartSec || 0) + clipDur(hit);
  }

  // Anything still running at the moment the anchor begins, in the order it sits on the
  // row. A clip that ends before then is untouched.
  const affected = others
    .filter((clip) => (clip.timelineStartSec || 0) + clipDur(clip) > anchorStart + OVERLAP_EPSILON)
    .sort((a, b) => (a.timelineStartSec || 0) - (b.timelineStartSec || 0));

  const pushed = new Map<string, number>();
  let cursor = anchorStart + clipDur(anchor);
  for (const clip of affected) {
    // Only ever later, never earlier: a clip already clear of the cursor stays put, and
    // the cascade stops naturally once the row has room again.
    const start = Math.max(clip.timelineStartSec || 0, cursor);
    if (start !== (clip.timelineStartSec || 0)) pushed.set(clip.id, start);
    cursor = start + clipDur(clip);
  }
  const anchorMoved = anchorStart !== (anchor.timelineStartSec || 0);
  if (!pushed.size && !anchorMoved) return clips;
  return clips.map((clip) => {
    if (clip.id === anchorId) return anchorMoved ? { ...clip, timelineStartSec: anchorStart } : clip;
    return pushed.has(clip.id) ? { ...clip, timelineStartSec: pushed.get(clip.id) as number } : clip;
  });
}

// The room a B-roll clip has to grow into without touching its neighbours, measured
// from where it currently sits. Used when resizing, which moves one edge rather than
// relocating the clip, so the neighbours are fixed.
export function overlayNeighbourBounds(
  timeline: EditorTimeline,
  clipId: string,
): { prevEnd: number; nextStart: number } {
  const laid = overlayLayout(timeline);
  const self = laid.find((entry) => entry.clip.id === clipId);
  if (!self) return { prevEnd: 0, nextStart: Infinity };
  let prevEnd = 0;
  let nextStart = Infinity;
  for (const other of laid) {
    if (other.clip.id === clipId) continue;
    if (other.end <= self.start + OVERLAP_EPSILON) prevEnd = Math.max(prevEnd, other.end);
    if (other.start >= self.end - OVERLAP_EPSILON) nextStart = Math.min(nextStart, other.start);
  }
  return { prevEnd, nextStart };
}

// Does any audio-track clip overlap the timeline span [startSec, endSec)? Used
// to auto-mute a main video clip that has narration/music over it (and un-mute
// one that doesn't, so its own sound plays).
export function audioCoversSpan(timeline: EditorTimeline, startSec: number, endSec: number): boolean {
  for (const track of timeline.audioTracks || []) {
    for (const c of track.clips) {
      const start = c.timelineStartSec;
      const end = start + clipDur(c);
      if (end > startSec + 0.01 && start < endSec - 0.01) return true;
    }
  }
  return false;
}

// Longest point anything reaches — the playable length of the whole timeline.
export function timelineDuration(timeline: EditorTimeline): number {
  let max = videoDuration(timeline);
  for (const laid of overlayLayout(timeline)) {
    max = Math.max(max, laid.end);
  }
  for (const track of timeline.audioTracks || []) {
    for (const c of track.clips || []) {
      max = Math.max(max, (c.timelineStartSec || 0) + clipDur(c));
    }
  }
  return max;
}

// Timeline position of a concrete clip piece. Video starts are derived from the
// sequential layout; overlay and audio starts are stored directly on each clip.
export function timelineClipStart(timeline: EditorTimeline, clipId: string): number | null {
  const video = videoLayout(timeline).find((entry) => entry.clip.id === clipId);
  if (video) return video.start;
  const overlay = overlayClips(timeline).find((clip) => clip.id === clipId);
  if (overlay) return overlay.timelineStartSec;
  for (const track of timeline.audioTracks) {
    const audio = track.clips.find((clip) => clip.id === clipId);
    if (audio) return audio.timelineStartSec;
  }
  return null;
}

// Replace one concrete timeline item with the two persisted saved clips created
// for a split. The left half keeps the selected timeline id; the right half gets
// a fresh id and, for audio, begins exactly where the left half ends.
export function splitTimelineClipWithSegments(
  timeline: EditorTimeline,
  clipId: string,
  segments: [ClipSegment, ClipSegment],
): EditorTimeline {
  const [leftSegment, rightSegment] = segments;
  if (!leftSegment || !rightSegment
    || leftSegment.sourceFileId !== rightSegment.sourceFileId
    || Math.abs(leftSegment.outSec - rightSegment.inSec) > 0.001) return timeline;

  const videoIndex = timeline.videoTrack.clips.findIndex((clip) => clip.id === clipId);
  if (videoIndex >= 0) {
    const original = timeline.videoTrack.clips[videoIndex];
    if (leftSegment.kind !== 'video' || rightSegment.kind !== 'video'
      || original.sourceFileId !== leftSegment.sourceFileId) return timeline;
    const left: TLVideoClip = {
      ...original,
      segmentId: leftSegment.segmentId,
      fileName: leftSegment.name || original.fileName,
      sourceInSec: leftSegment.inSec,
      sourceOutSec: leftSegment.outSec,
    };
    const right: TLVideoClip = {
      ...original,
      id: uid(),
      segmentId: rightSegment.segmentId,
      fileName: rightSegment.name || original.fileName,
      sourceInSec: rightSegment.inSec,
      sourceOutSec: rightSegment.outSec,
    };
    const clips = timeline.videoTrack.clips.slice();
    clips.splice(videoIndex, 1, left, right);
    return { ...timeline, videoTrack: { clips } };
  }

  const overlayIndex = overlayClips(timeline).findIndex((clip) => clip.id === clipId);
  if (overlayIndex >= 0) {
    const original = overlayClips(timeline)[overlayIndex];
    // Overlay media is video, so a split persists as video-kind saved clips.
    if (leftSegment.kind !== 'video' || rightSegment.kind !== 'video'
      || original.sourceFileId !== leftSegment.sourceFileId) return timeline;
    const left: TLOverlayClip = {
      ...original,
      segmentId: leftSegment.segmentId,
      fileName: leftSegment.name || original.fileName,
      sourceInSec: leftSegment.inSec,
      sourceOutSec: leftSegment.outSec,
    };
    const right: TLOverlayClip = {
      ...original,
      id: uid(),
      segmentId: rightSegment.segmentId,
      fileName: rightSegment.name || original.fileName,
      sourceInSec: rightSegment.inSec,
      sourceOutSec: rightSegment.outSec,
      timelineStartSec: original.timelineStartSec + (rightSegment.inSec - original.sourceInSec),
    };
    const clips = overlayClips(timeline).slice();
    clips.splice(overlayIndex, 1, left, right);
    return { ...timeline, overlayTrack: { clips } };
  }

  for (const track of timeline.audioTracks) {
    const audioIndex = track.clips.findIndex((clip) => clip.id === clipId);
    if (audioIndex < 0) continue;
    const original = track.clips[audioIndex];
    if (leftSegment.kind !== 'audio' || rightSegment.kind !== 'audio'
      || original.sourceFileId !== leftSegment.sourceFileId) return timeline;
    const left: TLAudioClip = {
      ...original,
      segmentId: leftSegment.segmentId,
      fileName: leftSegment.name || original.fileName,
      sourceInSec: leftSegment.inSec,
      sourceOutSec: leftSegment.outSec,
    };
    const right: TLAudioClip = {
      ...original,
      id: uid(),
      segmentId: rightSegment.segmentId,
      fileName: rightSegment.name || original.fileName,
      sourceInSec: rightSegment.inSec,
      sourceOutSec: rightSegment.outSec,
      timelineStartSec: original.timelineStartSec + (rightSegment.inSec - original.sourceInSec),
    };
    const audioTracks = timeline.audioTracks.map((candidate) => {
      if (candidate.id !== track.id) return candidate;
      const clips = candidate.clips.slice();
      clips.splice(audioIndex, 1, left, right);
      return { ...candidate, clips };
    });
    return { ...timeline, audioTracks };
  }
  return timeline;
}

export interface InlineAudioLane {
  id: string;
  label: string;
  note?: string;
  startSec: number;
  endSec: number;
  annotations: TranscriptSegment[];
  // Bar colour. Defaults to the timeline's audio teal; the aligned editor's
  // "B-roll covers this" band overrides it with the overlay purple.
  color?: string;
}

// Clicking the selected timeline clip again closes its editor. This applies to
// both the video lane and every audio lane.
export function toggleTimelineClipSelection(selectedId: string | null, clickedId: string) {
  return selectedId === clickedId ? null : clickedId;
}

// Remove the highlighted clip regardless of whether it is on the video track or
// an audio track. Returning the original object for a miss avoids a false undo
// entry when Delete is pressed with a non-clip timeline item selected.
export function removeClipById(timeline: EditorTimeline, clipId: string): EditorTimeline {
  let changed = false;
  const keep = (clip: { id: string }) => {
    if (clip.id !== clipId) return true;
    changed = true;
    return false;
  };
  const videoClips = timeline.videoTrack.clips.filter(keep);
  const overlayTrack = timeline.overlayTrack ? { clips: timeline.overlayTrack.clips.filter(keep) } : undefined;
  const audioTracks = timeline.audioTracks.map((track) => ({ ...track, clips: track.clips.filter(keep) }));
  return changed
    ? { ...timeline, videoTrack: { clips: videoClips }, ...(overlayTrack ? { overlayTrack } : {}), audioTracks }
    : timeline;
}

// Original source clips and named saved sub-clips are separate library items.
// A split may leave several timeline pieces with the same identity, so usage and
// removal operate on that identity rather than on one rendered piece.
export function timelineClipUsage(timeline: EditorTimeline): { sourceFileIds: string[]; segmentIds: string[] } {
  const sourceFileIds = new Set<string>();
  const segmentIds = new Set<string>();
  const record = (clip: TLVideoClip | TLOverlayClip | TLAudioClip) => {
    if (clip.segmentId) segmentIds.add(clip.segmentId);
    else sourceFileIds.add(clip.sourceFileId);
  };
  timeline.videoTrack.clips.forEach(record);
  overlayClips(timeline).forEach(record);
  timeline.audioTracks.forEach((track) => track.clips.forEach(record));
  return { sourceFileIds: Array.from(sourceFileIds).sort(), segmentIds: Array.from(segmentIds).sort() };
}

function removeMatchingClips(timeline: EditorTimeline, matches: (clip: TLVideoClip | TLOverlayClip | TLAudioClip) => boolean): EditorTimeline {
  let changed = false;
  const keep = <T extends TLVideoClip | TLOverlayClip | TLAudioClip>(clip: T) => {
    if (!matches(clip)) return true;
    changed = true;
    return false;
  };
  const videoClips = timeline.videoTrack.clips.filter(keep);
  const overlayTrack = timeline.overlayTrack ? { clips: timeline.overlayTrack.clips.filter(keep) } : undefined;
  const audioTracks = timeline.audioTracks.map((track) => ({ ...track, clips: track.clips.filter(keep) }));
  return changed
    ? { ...timeline, videoTrack: { clips: videoClips }, ...(overlayTrack ? { overlayTrack } : {}), audioTracks }
    : timeline;
}

export function removeSourceClips(timeline: EditorTimeline, sourceFileId: string): EditorTimeline {
  return removeMatchingClips(timeline, (clip) => !clip.segmentId && clip.sourceFileId === sourceFileId);
}

export function removeSegmentClips(timeline: EditorTimeline, segmentId: string): EditorTimeline {
  return removeMatchingClips(timeline, (clip) => clip.segmentId === segmentId);
}

// A source clip's known length, used to clamp trims. Null when Drive hasn't
// reported a duration yet (just-uploaded clip) — then trims aren't upper-bounded.
export function sourceLength(clip: VideoClip | undefined): number | null {
  return clip && clip.durationSec != null && isFinite(clip.durationSec) ? clip.durationSec : null;
}

// ── Constructors ──

// Fallback clip length when the source duration is unknown. Drive only reports a
// duration for VIDEO (videoMediaMetadata) — an audio file (e.g. an .mp3) comes back
// with durationSec=null, which would otherwise build a zero-length (invisible) clip.
// The real audio duration is probed on add (see TimelineEditor); this is the guard
// for when that probe fails, so the clip is always visible and trimmable.
const FALLBACK_CLIP_SEC = 15;

function clipLenOf(src: VideoClip): number {
  return src.durationSec != null && isFinite(src.durationSec) && src.durationSec > 0 ? src.durationSec : FALLBACK_CLIP_SEC;
}

export function videoClipFromSource(src: VideoClip): TLVideoClip {
  return {
    id: uid(),
    sourceFileId: src.fileId,
    fileName: src.name,
    sourceInSec: 0,
    sourceOutSec: clipLenOf(src),
    // Muted by default — the edit's sound comes from the audio layers (VO/music);
    // un-mute per clip when its own audio should be kept.
    muteAudio: true,
  };
}

// B-roll cutaway from a source clip: picture-only, positioned at timelineStartSec.
// maxDurationSec caps the initial length (e.g. to the covered main clip's length).
export function overlayClipFromSource(src: VideoClip, timelineStartSec: number, maxDurationSec?: number): TLOverlayClip {
  const len = maxDurationSec != null && maxDurationSec > 0
    ? Math.min(clipLenOf(src), maxDurationSec)
    : clipLenOf(src);
  return {
    id: uid(),
    sourceFileId: src.fileId,
    fileName: src.name,
    sourceInSec: 0,
    sourceOutSec: len,
    timelineStartSec: Math.max(0, timelineStartSec),
  };
}

export function audioClipFromSource(src: VideoClip, timelineStartSec: number): TLAudioClip {
  return {
    id: uid(),
    sourceFileId: src.fileId,
    fileName: src.name,
    sourceInSec: 0,
    sourceOutSec: clipLenOf(src),
    timelineStartSec: Math.max(0, timelineStartSec),
  };
}

// Add one named saved sub-clip. A sub-clip may appear on the timeline as many times as
// the edit wants — the same shot is often reused — so this places a copy rather than
// refusing when one is already there. fileName deliberately uses the editor-supplied
// name shown on the timeline bar.
export function addSegmentClipToTimeline(base: EditorTimeline, source: VideoClip, segment: ClipSegment): EditorTimeline {
  const fileName = segment.name?.trim() || segment.fileName || source.name;
  if (source.mimeType.startsWith('audio/')) {
    const tracks = base.audioTracks.map((track) => ({ ...track, clips: track.clips.slice() }));
    if (tracks.length === 0) tracks.push(newAudioTrack('Audio 1'));
    const track = tracks[tracks.length - 1];
    track.clips = [...track.clips, {
      ...audioClipFromSource(source, audioTrackEnd(track)),
      segmentId: segment.segmentId,
      fileName,
      sourceInSec: segment.inSec,
      sourceOutSec: segment.outSec,
    }];
    return { ...base, audioTracks: tracks };
  }
  const clip = {
    ...videoClipFromSource(source),
    segmentId: segment.segmentId,
    fileName,
    sourceInSec: segment.inSec,
    sourceOutSec: segment.outSec,
  };
  return { ...base, videoTrack: { clips: [...base.videoTrack.clips, clip] } };
}

export function newAudioTrack(name: string): TLAudioTrack {
  return { id: uid(), name, clips: [] };
}

// End of the last-placed clip on an audio track (where the next append should go).
export function audioTrackEnd(track: TLAudioTrack): number {
  return (track.clips || []).reduce((max, c) => Math.max(max, (c.timelineStartSec || 0) + clipDur(c)), 0);
}
